import moment from "moment-timezone"

export const userStatuses = Object.freeze({
  VERIFIED: "Verified",
  PENDING: "Pending",
  ERROR: "Error",
  UNKNOWN: "Unknown",
})

// Verified: user exists and has logged in since the account was last updated (created)
const userVerifiedIcon = "mdi-check-decagram-outline"
// Pending: user exists but has not logged in since the account was last updated (created)
const userPendingIcon = "mdi-account-clock-outline"
// Error: user doesn't exist or user exists but there was a failed login attempt
const userErrorIcon = "mdi-alert-circle-outline"
// Unknown: user not yet created
const userUnknownIcon = "mdi-account-question-outline"

const userStatusDateFormat = "MM-DD-YYYY, hh:mmA"

const formattedDateStringFrom = (dateString) => {
  return dateString ? moment(new Date(dateString)).format(userStatusDateFormat) : "never"
}

export const userStatusForEmail = ({ email, statusDataList }) => {
  return statusDataList?.find((statusObj) => statusObj.email === email) ?? null
}

const lastLoginDateFromUserStatus = (statusData) => {
  return statusData?.last_login ? new Date(statusData.last_login) : null
}

export const lastLoginFromUserStatus = (statusData) => {
  return formattedDateStringFrom(statusData?.last_login)
}

export const lastLoginFailureFromUserStatus = (statusData) => {
  return formattedDateStringFrom(statusData?.last_attempted_login)
}

export const loginCountFromUserStatus = (statusData) => {
  return statusData?.logins_count ?? 0
}

export const logsFromUserStatus = (statusData) => {
  return statusData?.logs ?? []
}

export const errorlogsFromUserStatus = (statusData) => {
  return (
    logsFromUserStatus(statusData).filter((log) => {
      const logHasError = log.details?.error ? true : false
      return logHasError
    }) ?? []
  )
}

export const userStatusIconForUserStatus = (status) => {
  let userIcon
  switch (status) {
    case userStatuses.VERIFIED:
      userIcon = userVerifiedIcon
      break
    case userStatuses.ERROR:
      userIcon = userErrorIcon
      break
    case userStatuses.PENDING:
      userIcon = userPendingIcon
      break
    default:
      userIcon = userUnknownIcon
      break
  }
  return userIcon
}

export const iconColorForUserStatus = (status) => {
  let userIconColor
  switch (status) {
    case userStatuses.VERIFIED:
      userIconColor = "green"
      break
    case userStatuses.ERROR:
      userIconColor = "red"
      break
    case userStatuses.PENDING:
      userIconColor = "yellow"
      break
    default:
      userIconColor = "white"
      break
  }
  return userIconColor
}

export const userStatusForGameDispatchTime = ({ statusData, dispatchTime }) => {
  let userStatus = userStatuses.PENDING

  let dispatchDate = dispatchTime ? new Date(dispatchTime) : null
  const lastLoginDate = lastLoginDateFromUserStatus(statusData)
  if (statusData) {
    const userHasLoggedIn = loginCountFromUserStatus(statusData) > 0
    const userHasLoggedInSinceGameStarted = dispatchDate && statusData ? lastLoginDate >= dispatchDate : false
    // const userHasLoginErrorSinceGameStarted = props.testSession?.dispatchTime ? thisUserData.lastAttemptedLoginTime >= props.testSession.dispatchTime : false
    let userHasErrorLogsSinceGameStarted = false
    let userHasErrorLogsSinceLastLogin = false
    errorlogsFromUserStatus(statusData).forEach((log) => {
      const errorLogDateStamp = new Date(log.date).getTime()
      const errorIsSinceGameDispatch = dispatchDate ? errorLogDateStamp >= dispatchDate : false
      const errorIsSinceLastLogin = lastLoginDate ? errorLogDateStamp >= lastLoginDate : false
      if (errorIsSinceLastLogin) userHasErrorLogsSinceLastLogin = true
      if (errorIsSinceGameDispatch) userHasErrorLogsSinceGameStarted = true
    })

    switch (true) {
      case userHasErrorLogsSinceLastLogin || (userHasErrorLogsSinceGameStarted && !userHasLoggedInSinceGameStarted):
        userStatus = userStatuses.ERROR
        break
      case userHasLoggedIn && userHasLoggedInSinceGameStarted:
        userStatus = userStatuses.VERIFIED
        break
      default:
        break
    }
  } else {
    userStatus = userStatuses.UNKNOWN
  }

  return userStatus
}

export const userStatusForGameListItem = ({ statusData, gameListItem }) => {
  let dispatchTimeString = gameListItem?.game_schedule ? `${gameListItem.game_schedule.start_date} ${gameListItem.game_schedule.start_time}` : null
  let dispatchDate = dispatchTimeString ? moment(dispatchTimeString, "MM-DD-YYYY hh:mmA").toDate() : null
  return userStatusForGameDispatchTime({ statusData, dispatchTime: dispatchDate.getTime() })
}

export const recentLogsFromUserStatus = (statusData) => {
  const logs = logsFromUserStatus(statusData)
  return (
    logs?.slice(0, 10).map((log) => {
      const logErrorMessage = log.details?.error?.message
      let logType = log.type
      if (logType === "s") {
        logType = "Successful Login"
      } else if (logType === "slo") {
        logType = "Successful Logout"
      }
      const logLocationCity = log.location_info?.city_name ?? "Unknown City"
      const logLocationCountry = log.location_info?.country_name ?? "Unknown Country"
      const logDate = log.date ? moment(new Date(log.date)).format(userStatusDateFormat) : "Unknown Date"
      if (logErrorMessage) {
        return `${logDate}: Error, ${logErrorMessage} (${logLocationCity}, ${logLocationCountry})`
      }
      return `${logDate}: ${logType} (${logLocationCity}, ${logLocationCountry})`
    }) ?? []
  )
}

// const userStatusTooltipContentForUser = (email) => {
//     const thisUserData = formUserData[email]
//     if (thisUserData) {
//         const logDescriptions = thisUserData.logs?.slice(0,10).map((log) => {
//             const logErrorMessage = log.details?.error?.message
//             let logType = log.type
//             if (logType === 's') {
//                 logType = 'Successful Login'
//             } else if (logType === 'slo') {
//                 logType = 'Successful Logout'
//             }
//             const logLocationCity = log.location_info?.city_name ?? "Unknown City"
//             const logLocationCountry = log.location_info?.country_name ?? "Unknown Country"
//             const logDate = log.date ?? 'Unknown Date'
//             if (logErrorMessage) {
//                 return `${logDate}: Error, ${logErrorMessage} (${logLocationCity}, ${logLocationCountry})`
//             }
//             return `${logDate}: ${logType} (${logLocationCity}, ${logLocationCountry})`
//         }) ?? []
//         return `
//         <p>User Status: ${userStatusForUser(email)}</p>
//         <p>Logins: ${thisUserData.loginsCount}</p>
//         <p>Last Login: ${thisUserData.lastLoginTime ? new Date(thisUserData.lastLoginTime).toISOString() : 'Never'}</p>
//         <p>Last Login Failure: ${thisUserData.lastAttemptedLoginTime ? new Date(thisUserData.lastAttemptedLoginTime) : 'Never'}</p>
//         <p>Recent Logs:</p>
//         ${logDescriptions.map((logMsg) => (`<p>${logMsg}</p>`)).join('\n')}
//         `
//     }
//     return `
//     <p>Status: ${userStatuses.UNKNOWN}</p>
//     <p>No User Data Available</p>
//     `
// }
